"use client";

import { type InputHTMLAttributes, type ReactNode, forwardRef } from "react";
import { Eye, EyeClosed } from "@phosphor-icons/react";
import config from "@/configs/config";
import { cn } from "@/shared/libs/commonUtils";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactNode;
  scrollOnFocus?: boolean;
  showPassword?: boolean;
  onTogglePassword?: () => void;
  classNames?: {
    inputClassName?: string;
    iconClassName?: string;
  };
}

const { dir } = config;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      classNames = {},
      className,
      type,
      icon,
      scrollOnFocus = false,
      showPassword,
      onTogglePassword,
      ...props
    },
    ref,
  ) => {
    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;
    const { iconClassName, inputClassName } = classNames;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (scrollOnFocus) {
        e.target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      props.onFocus?.(e);
    };

    return (
      <div dir={dir} className="relative w-full">
        <input
          autoComplete="off"
          type={inputType}
          className={cn(
            "flex h-12 w-full rounded-md border border-border bg-transparent px-3 py-1 text-sm shadow-none transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
            inputClassName ?? className,
          )}
          ref={ref}
          onFocus={handleFocus}
          {...props}
        />

        {icon && (
          <div
            className={cn(
              `absolute top-0 h-full flex items-center justify-center ${
                dir === "rtl" ? "left-3" : "right-2"
              }`,
            )}
          >
            {icon}
          </div>
        )}

        {isPassword && onTogglePassword && (
          <button
            type="button"
            className="absolute left-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={onTogglePassword}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeClosed className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
